import { useMemo, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { ArrowLeft, ArrowRight, Dices } from 'lucide-react';
import { useArchiveTagCounts } from '@/hooks/useArchiveTagCounts';
import { usePinnedNotes } from '@/hooks/usePinnedNotes';
import { cn } from '@/lib/utils';

interface RecordNavProps {
  /** The record being read, or null/undefined while it is still loading. */
  current?: NostrEvent | null;
  /** Called when "Archive index" is clicked (lets the page return to the feed's scroll spot). */
  onArchive?: (e: MouseEvent) => void;
  className?: string;
}

const pill = cn(
  'inline-flex h-10 items-center justify-center gap-2 rounded-full border border-foreground/40 bg-card px-3.5 text-foreground sm:px-4',
  'font-engraved text-[11px] font-bold uppercase tracking-[0.14em] shadow-[0_1px_2px_rgba(40,25,10,0.1)] transition-colors',
  'hover:bg-foreground/[0.07] active:bg-foreground/[0.12] [-webkit-tap-highlight-color:transparent]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'aria-disabled:pointer-events-none aria-disabled:opacity-40 disabled:pointer-events-none disabled:opacity-40',
);

/**
 * The row under or over a record: Archive index, Random and Next, centred.
 *
 *   - Archive index: back to the feed.
 *   - Random: another record chosen at random from the whole archive.
 *   - Next: the next older record, in the same order as the feed (newest first).
 *
 * Random and Next replace the current page in history rather than adding to it,
 * so the browser's Back button (and "Archive index") still returns to the feed
 * in one step, however many records the reader has moved through.
 */
export function RecordNav({ current, onArchive, className }: RecordNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: tagData } = useArchiveTagCounts();
  const { data: pinnedNotes } = usePinnedNotes();

  // The whole archive, newest first: what the feed is built from.
  const archive = useMemo(() => {
    const byId = new Map<string, NostrEvent>();
    for (const n of tagData?.notes ?? []) byId.set(n.id, n);
    for (const n of pinnedNotes ?? []) if (!byId.has(n.id)) byId.set(n.id, n);
    return Array.from(byId.values()).sort((a, b) => b.created_at - a.created_at);
  }, [tagData, pinnedNotes]);

  // The next older record. If this one isn't in the list (a record opened from a
  // shared link that the feed hides), take the first one older than it.
  const next = useMemo(() => {
    if (!current) return null;
    const i = archive.findIndex((n) => n.id === current.id);
    if (i >= 0) return archive[i + 1] ?? null;
    return archive.find((n) => n.created_at < current.created_at) ?? null;
  }, [archive, current]);

  const hasOthers = archive.some((n) => n.id !== current?.id);

  const open = (note: NostrEvent) =>
    navigate(`/${nip19.noteEncode(note.id)}`, { replace: true, state: location.state });

  const openRandom = () => {
    const pool = archive.filter((n) => n.id !== current?.id);
    if (pool.length === 0) return;
    open(pool[Math.floor(Math.random() * pool.length)]);
  };

  return (
    <nav
      aria-label="Move between records"
      className={cn('flex flex-wrap items-center justify-center gap-2', className)}
    >
      <Link to="/" onClick={onArchive} className={pill}>
        <ArrowLeft aria-hidden="true" className="h-[18px] w-[18px]" />
        <span>
          Archive<span className="hidden sm:inline"> index</span>
        </span>
      </Link>

      <button type="button" onClick={openRandom} disabled={!hasOthers} aria-label="Random record" className={pill}>
        <Dices aria-hidden="true" className="h-[18px] w-[18px]" />
        <span aria-hidden="true">Random</span>
      </button>

      <button
        type="button"
        onClick={() => next && open(next)}
        disabled={!next}
        aria-label="Next record (older)"
        className={pill}
      >
        <span aria-hidden="true">Next</span>
        <ArrowRight aria-hidden="true" className="h-[18px] w-[18px]" />
      </button>
    </nav>
  );
}
